// ---------- filled-hatching alphabet (intaglio, closed) ----------
//   0        case vide, du papier nu
//   1..4     hachure espacée, orientation 0° / 45° / 90° / 135°
//   5..8     hachure moyenne, mêmes orientations
//   9..12    hachure serrée, mêmes orientations
//   13       croisillon (deux sens serrés)
//   14       triple croisillon (trois sens serrés)
//   15       case pleine, une hachure espacée réservée en blanc
//   16       case pleine, sans réserve
//
// POURQUOI IL EXISTE, et pas juste un réglage de `hachures`. `hachures.encode.ts` trame sur
// `steps = [0, cover(1)/crosshatch, cover(5)/crosshatch, 1]` : une division par la couverture du
// croisillon qui le décrète 1,0 par convention. Or deux familles de traits qui se recouvrent en
// partie ne couvrent jamais la case entière — mesuré, 0,60 — donc « noir » y veut dire 0,60
// d'encre, et la feuille est écrasée dans son amplitude comme l'était `des` avant `desPlein`. Le
// remède est le même, dans le même ordre : lever le plafond (une case pleine existe enfin), puis
// laisser l'échelle littérale (la couverture mesurée, pas divisée).
//
// Seize codes plutôt que treize : trois écartements (espacé, moyen, serré) au lieu de deux
// donnent douze hachures orientées au lieu de huit, et un troisième sens de croisillon referme
// une partie de l'écart avant la case pleine — la rampe mesurée ne saute jamais de plus de
// 0,17 d'un palier au suivant, quand un simple ajout de case pleine (`hachures + case pleine`
// dans la planche d'essai) en laissait un de 0,40.
//
// La réserve du code 15 n'est PAS un croisillon : réserver deux sens retire ~0,42 de l'encre du
// fond (1 − (1 − l)² à l'écartement espacé) et ne laisse que ~0,58 — plus clair que le croisillon
// qu'il est censé assombrir. Une seule famille ne retire que ~0,24 et laisse ~0,76 : un vrai
// rehaut entre le croisillon et le plein, mesuré sur la planche `essais/alphabets-tableaux/hachures.html`
// avant d'être porté ici.
//
// Identifiant neuf plutôt que `hachures` modifié en place, par prudence et non par nécessité :
// les codes 1 à 9 ne changent pas de sens (contrairement aux dés, qui renumérotaient), donc rien
// ne casse pour le catalogue déjà publié. Mais un vieux client qui verrait un code 10+ sous
// l'identifiant `hachures` ne planterait pas — `(code - 1) % 4` reste un indice valide — il
// dessinerait juste la mauvaise chose, silencieusement. Le filtre de catalogue par alphabets
// déclarés (`knownAlphabetIds`, cf. `index.ts`) rend ce cas impossible tant que le puzzle porte
// un identifiant que ce vieux client ne déclare pas.

import { inkColor } from '../ink';
import type { Alphabet } from './types';

const SPACING = [0.5, 0.4, 0.32] as const; // espacée / moyenne / serrée, fraction de la case
const WIDTH = 0.12; // largeur du trait, fraction de la case, comme `hachures`

/** Porté tel quel de hachures.ts : une famille de traits parallèles, calée sur un repère global
 *  pour que deux cases voisines de même code se lisent comme une seule trame. */
function drawLineFamily(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  dir: number,
  spacing: number,
): void {
  const a = (dir * Math.PI) / 4;
  const dx = Math.cos(a);
  const dy = Math.sin(a);
  const nx = -dy;
  const ny = dx;
  const t1 = x * nx + y * ny;
  const t2 = (x + w) * nx + y * ny;
  const t3 = x * nx + (y + h) * ny;
  const t4 = (x + w) * nx + (y + h) * ny;
  const tmin = Math.min(t1, t2, t3, t4);
  const tmax = Math.max(t1, t2, t3, t4);
  for (let k = Math.ceil(tmin / spacing); k * spacing <= tmax; k++) {
    const px = k * spacing * nx;
    const py = k * spacing * ny;
    let ua = -1e9;
    let ub = 1e9;
    if (Math.abs(dx) > 1e-9) {
      let a1 = (x - px) / dx;
      let a2 = (x + w - px) / dx;
      if (a1 > a2) {
        const s = a1;
        a1 = a2;
        a2 = s;
      }
      if (a1 > ua) ua = a1;
      if (a2 < ub) ub = a2;
    } else if (px < x || px > x + w) continue;
    if (Math.abs(dy) > 1e-9) {
      let b1 = (y - py) / dy;
      let b2 = (y + h - py) / dy;
      if (b1 > b2) {
        const s = b1;
        b1 = b2;
        b2 = s;
      }
      if (b1 > ua) ua = b1;
      if (b2 < ub) ub = b2;
    } else if (py < y || py > y + h) continue;
    if (ub <= ua) continue;
    ctx.moveTo(px + ua * dx, py + ua * dy);
    ctx.lineTo(px + ub * dx, py + ub * dy);
  }
}

/** Trace une ou plusieurs familles de traits dans la case (x,y,w,h), dans la couleur `css` —
 *  jamais `inkColor()` implicite, pour que le rehaut réservé du code 15 puisse tracer en papier. */
function strokeFamilies(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  dirs: readonly number[],
  spacing: number,
  css: string,
): void {
  ctx.strokeStyle = css;
  ctx.lineWidth = Math.min(w, h) * WIDTH;
  ctx.beginPath();
  for (const d of dirs) drawLineFamily(ctx, x, y, w, h, d, Math.min(w, h) * spacing);
  ctx.stroke();
}

/** Le papier de la charte, pour la réserve du code 15 — cf. la même fonction dans desPlein.ts. */
let paperCss: string | null = null;
function paper(): string {
  if (paperCss) return paperCss;
  const v = getComputedStyle(document.documentElement).getPropertyValue('--paper').trim();
  if (v) paperCss = v;
  return v || '#fbf6ec';
}

const TIER_NAME = ['espacée', 'moyenne', 'serrée'];
const DIR_NAME = ['horizontale', 'diagonale ↘', 'verticale', 'diagonale ↙'];

export const hachuresPleines: Alphabet = {
  id: 'hachuresPleines',
  label: 'Hachures pleines',
  symbolName: 'hachure',
  helpText:
    'Taille-douce, en <b>seize codes</b> : trois serrages de hachures pour les demi-teintes, un ' +
    'croisillon puis un <b>triple croisillon</b> pour les ombres, et une case pleine avec un ' +
    '<b>rehaut réservé</b> en blanc pour finir au noir — comme un graveur qui gratte une taille ' +
    'claire dans une ombre trop dense. Les traits sont calés sur un repère global, donc la ' +
    "trame reste continue d'une case à l'autre.",

  aspect: () => 1,

  // Croissant par construction : chaque palier de serrage/densité couvre plus que le
  // précédent (mesuré sur la planche d'essai), et l'ordre au sein d'un même palier — les
  // quatre orientations — n'a pas d'importance, leurs couvertures étant quasi identiques.
  paletteOrder: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],

  codeName: (code) => {
    if (code === 16) return 'case pleine';
    if (code === 15) return 'case pleine, hachure en réserve';
    if (code === 14) return 'triple croisillon';
    if (code === 13) return 'croisillon';
    const tier = TIER_NAME[Math.floor((code - 1) / 4)];
    return `hachure ${tier}, ${DIR_NAME[(code - 1) % 4]}`;
  },

  draw(ctx, x, y, w, h, code) {
    if (!code) return;
    if (code === 16) {
      ctx.fillStyle = inkColor();
      ctx.fillRect(x, y, w, h);
      return;
    }
    if (code === 15) {
      ctx.fillStyle = inkColor();
      ctx.fillRect(x, y, w, h);
      strokeFamilies(ctx, x, y, w, h, [1], SPACING[0], paper());
      return;
    }
    if (code === 14) {
      strokeFamilies(ctx, x, y, w, h, [0, 1, 3], SPACING[2], inkColor());
      return;
    }
    if (code === 13) {
      strokeFamilies(ctx, x, y, w, h, [1, 3], SPACING[2], inkColor());
      return;
    }
    const dir = (code - 1) % 4;
    const tier = Math.floor((code - 1) / 4);
    strokeFamilies(ctx, x, y, w, h, [dir], SPACING[tier], inkColor());
  },
};

function lum(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function hexOrRgbToLum(css: string): number {
  if (css[0] !== '#') {
    const [r, g, b] = css.match(/\d+/g)!.map(Number);
    return lum(r, g, b);
  }
  const h = css.slice(1);
  const n = parseInt(
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h,
    16,
  );
  return lum((n >> 16) & 255, (n >> 8) & 255, n & 255);
}

let steps: number[] | null = null;

/**
 * Couverture mesurée des sept paliers non nuls, sur un plateau de 3x3 cases.
 *
 * Six d'entre eux ne peignent que de l'encre sur du papier nu : le canal alpha suffit,
 * indépendant de l'encre choisie, comme le fait déjà `hachures.ts::measure()`. Le septième — la
 * réserve du code 15 — peint un fond puis y réserve des traits clairs : partout opaque, le canal
 * alpha y mesurerait 1 quel que soit le fond réservé. Sa couverture se lit en LUMINANCE, entre
 * le papier et l'encre choisie, comme `effectiveColors`/`coverOf` le font pour les dés retournés.
 */
function measure(): void {
  const S = 40;
  const n = 3;
  const c = document.createElement('canvas');
  c.width = c.height = S * n;
  const x = c.getContext('2d')!;

  const alphaCover = (
    draw1: (ctx: CanvasRenderingContext2D, x: number, y: number, s: number) => void,
  ) => {
    x.clearRect(0, 0, S * n, S * n);
    for (let r = 0; r < n; r++) for (let cc = 0; cc < n; cc++) draw1(x, cc * S, r * S, S);
    const d = x.getImageData(0, 0, S * n, S * n).data;
    let a = 0;
    for (let i = 3; i < d.length; i += 4) a += d[i];
    return a / (255 * S * S * n * n);
  };

  const inkCover = (
    draw1: (ctx: CanvasRenderingContext2D, x: number, y: number, s: number) => void,
  ) => {
    x.fillStyle = paper();
    x.fillRect(0, 0, S * n, S * n);
    for (let r = 0; r < n; r++) for (let cc = 0; cc < n; cc++) draw1(x, cc * S, r * S, S);
    const d = x.getImageData(0, 0, S * n, S * n).data;
    let L = 0;
    const m = d.length / 4;
    for (let i = 0; i < d.length; i += 4) L += lum(d[i], d[i + 1], d[i + 2]);
    const Lp = hexOrRgbToLum(paper());
    const Li = hexOrRgbToLum(inkColor());
    return (Lp - L / m) / (Lp - Li || 1);
  };

  const family =
    (dirs: readonly number[], spacing: number) =>
    (ctx: CanvasRenderingContext2D, x: number, y: number, s: number) =>
      strokeFamilies(ctx, x, y, s, s, dirs, spacing, inkColor());

  const espacee = alphaCover(family([0], SPACING[0]));
  const moyenne = alphaCover(family([0], SPACING[1]));
  const serree = alphaCover(family([0], SPACING[2]));
  const croisillon = alphaCover(family([1, 3], SPACING[2]));
  const triple = alphaCover(family([0, 1, 3], SPACING[2]));
  const reserve = inkCover((ctx, x, y, s) => {
    ctx.fillStyle = inkColor();
    ctx.fillRect(x, y, s, s);
    strokeFamilies(ctx, x, y, s, s, [1], SPACING[0], paper());
  });

  steps = [0, espacee, moyenne, serree, croisillon, triple, reserve, 1];
}

/** Les huit paliers mesurés, pour hachuresPleines.encode.ts. */
export function hachuresPleinesSteps(): number[] {
  if (!steps) measure();
  return steps!;
}
